import { Button, Modal } from "react-bootstrap";
import React from "react";
import { identity } from "lodash";
import { PendingExternalQuotationItem } from "../../types/models/quotation_item";
import { genericNumberChange, genericTextChange, wrapFieldErrors } from "../../utils/forms";
import Api from '../../service/api';
import { CreateExternalQuotationRequestBody } from "../../types/api/requests/quotation_item";
import { extractAxiosError } from "../../utils/error";
import { checkIntForError, checkStringForError } from "../../lib/validation";
import TimeoutAlert from "../timeout_alert";
import { InputFieldWithValidation, TextAreaWithValidation } from "../fields_with_validation";
import {isSearchSupported, searchSupportedUrl} from "../../lib/search";

interface RequestExternalQuotationModalProps {
  show: boolean;
  onSuccess: (quotationItem: PendingExternalQuotationItem) => void;
  onCancel: () => void;
  requestLink?: string;
  showLinkWarning?: boolean;
}

type FieldErrors = Partial<Record<'quantity' | 'link' | 'instructions', string[]>>;

export default function RequestExternalQuotationModal({ show, onSuccess, onCancel, requestLink, showLinkWarning }: RequestExternalQuotationModalProps) {
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [quantity, setQuantity] = React.useState('1');
  const [link, setLink] = React.useState('');
  const [instructions, setInstructions] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!show || !requestLink) {
      return;
    }

    setLink(requestLink);
  }, [requestLink, show]);

  const resetFields = React.useCallback(() => {
    setQuantity('1');
    setLink('');
    setInstructions('');
  }, []);

  const validateFields = () => {
    const errors: Required<FieldErrors> = {
      quantity: [],
      link: [],
      instructions: []
    };

    errors.quantity = checkIntForError(quantity, {
      gt: 0,
      attrName: 'quantity'
    });
    errors.link = checkStringForError(link, {
      required: true,
      attrName: 'link',
      minLength: 1
    });

    if (errors.link.length === 0) {
      try {
        new URL(link);
      } catch (e) {
        errors.link.push("Please enter a valid link");
      }
    }

    errors.instructions = checkStringForError(instructions, {
      required: true,
      attrName: 'instructions',
      minLength: 1
    });

    setErrors(errors);
    const isInvalid = Object.values(errors).some(value => value.length > 0);
    return !isInvalid;
  };

  const successButtonHandler = async () => {
    // validate fields
    const isValid = validateFields();
    if (!isValid) {
      return;
    }

    // call api
    const reqData: CreateExternalQuotationRequestBody = {
      instructions: instructions.trim(),
      link: link.trim(),
      quantity: parseInt(quantity)
    };

    setSubmitting(true);
    try {
      const url = new URL(link);
      if (isSearchSupported(url)) {
        searchSupportedUrl(url);
        return;
      }
    } catch (e) {}



    try {
      setSubmitting(true);
      const res = await Api.quotationItem.createExternal(reqData);
      // call callback with result
      onSuccess(res.data.quotationItem);
      resetFields();
    } catch (err: any) {
      const message = extractAxiosError(err);
      setErrorMessage(message);
    }
    setSubmitting(false);
  };

  return <Modal
    show={show}
    onHide={onCancel}
    size={'lg'}
    centered
  >
    <Modal.Header closeButton>
      <Modal.Title>You are about to request for a product quotation with us!</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <TimeoutAlert
        message={errorMessage}
        onHide={() => setErrorMessage(null)}
      />

      Please provide the details of the products that you wish for us to quote for you.
      <br />
      <br />

      {showLinkWarning && (
        <TimeoutAlert
          message="Please double check the link before submitting as it might not be the full url you have entered."
          alertColor="warning"
          timeoutDuration={0}
          onHide={identity}
        />
      )}

      <div className="form-group">
        <label className="form-label">Link <span className="text-danger">*</span></label>
        <InputFieldWithValidation
          type="text"
          className="form-control"
          errors={errors.link}
          value={link}
          onChange={wrapFieldErrors(errors, setErrors, 'link', genericTextChange(setLink))}
          placeholder="https://www.instagram.com/p/CiBNPCEPIyg/?igshid=YmMyMTA2M2Y%3D"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Quantity <span className="text-danger">*</span></label>
        <InputFieldWithValidation
          type="number"
          className="form-control"
          errors={errors.quantity}
          value={quantity}
          onChange={wrapFieldErrors(errors, setErrors, 'quantity', genericNumberChange(setQuantity))}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Instructions <span className="text-danger">*</span></label>
        <TextAreaWithValidation
          className="form-control"
          errors={errors.instructions}
          value={instructions}
          onChange={wrapFieldErrors(errors, setErrors, 'instructions', genericTextChange(setInstructions))}
          placeholder="Provide us details of the purchase to be made. For example, size, color or any instruction that pertains to the product you're getting."
        />
      </div>

      The product link you provided us hasn't yet partnered with our platform.
      <br />
      <br />
      However, we wouldn't want to lose you! Let us know the quantity and any variations and we'll quote you within <strong>2 business days</strong> after the seller has provided us with the necessary details!
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onCancel} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={successButtonHandler} disabled={submitting}>
        Proceed
      </Button>
    </Modal.Footer>
  </Modal>;
}